/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { BOT_DEPLOYMENTS_CHANNELS } from '../../../constants/main';

import { useLazyFetchChapterSourcesQuery } from '../../../store/chapterSources/chapterSourcesApi';
import { useLazyFetchBotDeploymentsQuery } from '../../../store/botDeployments/botDeploymentsApi';
import { NODE_TYPES } from '../../../constants/flowGraph';

/**
 * This module provides a custom React hook for checking references related to certain entities, such as chapter sources and deployments.
 * Mostly used when deleting an entity, to check if it's referenced by other entities.
 *
 * @example
 * ```jsx
 * import { useDeletionProcess } from './useDeletionProcess';
 *
 * const MyComponent = () => {
 *   const { isLoading, hasReference, checkForReferences } = useDeletionProcess();
 *
 *   // ...
 * }
 * ```
 */
export const useReferenceChecker = () => {
  const { tenantId, orgId } = useParams();
  const [relatedIds, setRelatedIds] = useState([]);
  const [deploymentsRelatedIds, setDeploymentsRelatedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasReference, setHasReference] = useState(false);
  const [triggerChapterSourcesFetch] = useLazyFetchChapterSourcesQuery();
  const [triggerDeploymentsFetch] = useLazyFetchBotDeploymentsQuery();

  const checkForActivityReferences = async (chapterSourceIds) => {
    setIsLoading(true);
    const { items: chaptersources } = await triggerChapterSourcesFetch({ orgId, tenantId }).unwrap();
    const { items: deployments } = await triggerDeploymentsFetch({ orgId, tenantId }).unwrap();

    let introNodeIDs = [];
    // Get the id of the automatedAnswer that is used in chapter sources for reference
    if (Array.isArray(chapterSourceIds)) {
      introNodeIDs = chapterSourceIds.map(automatedAnswerId => chaptersources.find(chaptersource =>
        chaptersource.id === automatedAnswerId)?.content.nodes.find(node =>
        node.type === NODE_TYPES.INTRO).id
      );
    } else {
      introNodeIDs = [chaptersources.find(chaptersource =>
        chaptersource.id === chapterSourceIds)?.content.nodes.find(node =>
        node.type === NODE_TYPES.INTRO).id];
    }

    const chapterSourcesRefs = [];
    introNodeIDs.forEach(introNodeId => {
      // Find the actual references in chapter sources
      const refs = chaptersources.filter(chaptersource => (
        chaptersource.content.nodes.filter(node => node.type === NODE_TYPES.JUMP && node.jumpTo === introNodeId).length > 0
      ));
      if (refs.length === 0) {
        return;
      }
      chapterSourcesRefs.push(refs);
    });

    // Find the actual references in deployments
    const webChatDeployments = deployments.filter(deployment => deployment.platform === BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key);

    const deploymentsRefs = [];

    introNodeIDs.forEach(introNodeId => {
      // Find the actual references in chapter sources
      const refs = webChatDeployments.filter(deployment => deployment?.settings?.layout?.microsoftWebChat?.configuration?.entryPoint === introNodeId);
      if (refs.length === 0) {
        return;
      }
      deploymentsRefs.push(refs);
    });

    if (chapterSourcesRefs.length > 0 || deploymentsRefs.length > 0) {
      setRelatedIds(chapterSourcesRefs.map(chaptersourceRef => chaptersourceRef[0].id));
      setDeploymentsRelatedIds(deploymentsRefs.map(deploymentRef => deploymentRef[0].displayName));
      setHasReference(true);
      setIsLoading(false);
      return true;
    } else {
      setRelatedIds([]);
      setDeploymentsRelatedIds([]);
      setHasReference(false);
      setIsLoading(false);
      return false;
    }
  };

  const checkForAAReferences = async (chaptersourceIds) => {
    setIsLoading(true);
    const { items: chaptersources } = await triggerChapterSourcesFetch({ orgId, tenantId }).unwrap();

    let introNodeIDs = [];
    // Get the id of the Automated Answer that is used in chaptersources for reference
    if (Array.isArray(chaptersourceIds)) {
      introNodeIDs = chaptersourceIds.map(automatedAnswerId => chaptersources.find(chaptersource =>
        chaptersource.id === automatedAnswerId)?.content.nodes.find(node =>
        node.type === NODE_TYPES.INTRO).id
      );
    } else {
      // If chaptersourceIds is not an array
      introNodeIDs = [chaptersources.find(chaptersource =>
        chaptersource.id === chaptersourceIds)?.content.nodes.find(node =>
        node.type === NODE_TYPES.INTRO).id];
    }

    // Find the actual references
    const automatedAnswerRefs = [];
    introNodeIDs.forEach(introNodeId => {
      const refs = chaptersources.filter(chaptersource => (
        chaptersource.content.nodes.filter(node => node.type === NODE_TYPES.JUMP && node.jumpTo === introNodeId).length > 0
      ));
      if (refs.length === 0) {
        return;
      }
      automatedAnswerRefs.push(refs);
    });

    if (automatedAnswerRefs.length > 0) {
      setRelatedIds(automatedAnswerRefs.map(automatedAnswerRef => automatedAnswerRef[0].id));
      setHasReference(true);
      setIsLoading(false);
      return true;
    } else {
      setRelatedIds([]);
      setHasReference(false);
      setIsLoading(false);
      return false;
    }
  };

  const resetReferences = () => {
    setRelatedIds([]);
    setIsLoading(false);
    setHasReference(false);
  };

  return {
    isLoading,
    relatedIds,
    hasReference,
    resetReferences,
    checkForAAReferences,
    deploymentsRelatedIds,
    checkForActivityReferences
  };
};
